import React from 'react';
import { PayoutBreakdown } from '../../types/career';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { DirectLineItem } from './DirectLineItem';
import { TaxSummaryCard } from './TaxSummaryCard';
import { formatAE, formatCurrencyUAH, formatCurrencyUSD, formatSimulatedMonthDate } from '../../utils/formatters';
import { getLevelTitle, isCoOwnerLevel, isDirectorLevel } from '../../math/careerRules';
import { useSimulationStore } from '../../store/useSimulationStore';
import {
  FileText,
  UserCheck,
  GitFork,
  Award,
  Crown,
  Calculator,
  Percent,
  Calendar,
} from 'lucide-react';

export interface CheckBreakdownTableProps {
  payout: PayoutBreakdown;
  consultantName?: string;
  simulationMonth?: number;
  onSelectLeader?: (leaderId: string) => void;
  rateCoefficient?: number;
  onRateCoefficientChange?: (coeff: number) => void;
  className?: string;
}

export const CheckBreakdownTable: React.FC<CheckBreakdownTableProps> = ({
  payout,
  simulationMonth,
  onSelectLeader,
  rateCoefficient: propRateCoefficient,
  onRateCoefficientChange,
  className = '',
}) => {
  const storeRateCoeff = useSimulationStore((state) => state.commissionRateCoefficient);
  const setStoreRateCoeff = useSimulationStore((state) => state.setCommissionRateCoefficient);
  const simulationStartDate = useSimulationStore((state) => state.simulationStartDate);

  const activeCoeff = propRateCoefficient ?? payout.rateCoefficient ?? storeRateCoeff ?? 1.0;

  const handleToggleRate = (coeff: number) => {
    if (onRateCoefficientChange) {
      onRateCoefficientChange(coeff);
    } else {
      setStoreRateCoeff(coeff);
    }
  };

  const levelTitle = getLevelTitle(payout.level);
  const isDirector = isDirectorLevel(payout.level);
  const isCoOwner = isCoOwnerLevel(payout.level);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Main Statement Card */}
      <Card variant="elevated" className="overflow-hidden border-slate-700/80">
        {/* Header Banner */}
        <CardHeader className="bg-gradient-to-r from-slate-900 via-slate-800/80 to-slate-900 border-b border-white/10 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-accent-emerald/15 border border-accent-emerald/30 flex items-center justify-center text-accent-emerald flex-shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base sm:text-lg text-text-primary whitespace-nowrap">
                  Деталізація чеку
                </CardTitle>
                {simulationMonth !== undefined && (
                  <div className="mt-1 inline-flex flex-col px-2 py-0.5 rounded-md bg-white/5 border border-white/10 font-mono text-left w-fit">
                    <span className="text-[11px] font-bold text-accent-neon leading-tight">
                      Місяць {simulationMonth}
                    </span>
                    <span className="text-[10.5px] text-text-muted flex items-center gap-1 leading-tight mt-0.5 whitespace-nowrap">
                      <Calendar className="w-2.5 h-2.5 text-accent-emerald shrink-0" />
                      <span>{formatSimulatedMonthDate(simulationMonth, simulationStartDate)}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
              <Badge level={payout.level} size="lg">
                Рівень {payout.level} • {levelTitle}
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-3.5 sm:p-4 space-y-5">
          {/* Commission Rate Coefficient Switcher */}
          <div
            data-tour="commission-toggle"
            className="p-3 sm:p-3.5 rounded-xl bg-slate-900/90 border border-white/10 space-y-2.5"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                <Percent className="w-3.5 h-3.5 text-accent-neon flex-shrink-0" />
                <span>Коефіцієнт комісії:</span>
              </div>
              <span className="font-mono text-xs text-accent-neon font-bold px-2 py-0.5 rounded bg-accent-neon/10 border border-accent-neon/20">
                {Math.round(activeCoeff * 100)}%
              </span>
            </div>

            <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-slate-950 border border-white/10 w-full">
              <button
                type="button"
                data-tour="commission-100-btn"
                onClick={() => handleToggleRate(1.0)}
                className={`w-full px-3 py-2 rounded-md text-xs font-medium transition-all cursor-pointer select-none text-center ${
                  activeCoeff === 1.0
                    ? 'bg-accent-emerald text-slate-950 font-bold shadow-[0_0_10px_rgba(16,185,129,0.35)]'
                    : 'text-text-secondary hover:text-white'
                }`}
                title="Повний розрахунок 100%"
              >
                100% (Повний чек)
              </button>
              <button
                type="button"
                data-tour="commission-80-btn"
                onClick={() => handleToggleRate(0.8)}
                className={`w-full px-3 py-2 rounded-md text-xs font-medium transition-all cursor-pointer select-none text-center ${
                  activeCoeff === 0.8
                    ? 'bg-cyan-500 text-slate-950 font-bold shadow-[0_0_10px_rgba(6,182,212,0.35)]'
                    : 'text-text-secondary hover:text-white'
                }`}
                title="Виплата в 1-й рік (80%)"
              >
                80% (1-й рік)
              </button>
            </div>
          </div>

          {/* SECTION 1: Personal Sales Commission */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs sm:text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-accent-emerald flex-shrink-0" />
                <span>1. Особисті продажі (Personal Sales)</span>
              </h4>
              <span className="text-xs sm:text-sm font-mono font-bold text-accent-neon whitespace-nowrap flex-shrink-0">
                {formatCurrencyUAH(payout.personalCommissionUAH, 2)}
              </span>
            </div>

            <div className="glass-card rounded-xl p-3.5 sm:p-4 border border-white/5 space-y-2.5 text-xs">
              <div className="space-y-1">
                <div className="text-text-primary font-medium">
                  Прямі договори консультанта: <strong className="font-mono text-accent-emerald">{formatAE(payout.personalAE)}</strong>
                </div>
                <div className="text-text-secondary">
                  Базова ставка рівня: <span className="font-mono">{formatCurrencyUAH(payout.personalRateUAH, 2)}/Е</span>
                  {activeCoeff !== 1.0 && (
                    <>
                      {' '}• Ефективна ставка ({Math.round(activeCoeff * 100)}%):{' '}
                      <span className="font-mono text-text-primary font-semibold">
                        {formatCurrencyUAH(payout.personalRateUAH * activeCoeff, 2)}/Е
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Full-width responsive formula box - stacked cleanly to never overflow narrow containers */}
              <div className="w-full bg-slate-900/80 rounded-lg p-2.5 border border-white/5 font-mono space-y-1">
                <div className="text-[11px] text-text-muted break-words leading-snug">
                  Формула: {formatAE(payout.personalAE, 0)} × {formatCurrencyUAH(payout.personalRateUAH, 2)}
                  {activeCoeff !== 1.0 ? ` × ${activeCoeff}` : ''}
                </div>
                <div className="text-sm font-bold text-accent-emerald whitespace-nowrap text-right pt-0.5">
                  = {formatCurrencyUAH(payout.personalCommissionUAH, 2)}
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: Direct Line Inter-Level Commissions */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-xs sm:text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                <GitFork className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span>2. Комісія з прямих гілок (Inter-Level Lines)</span>
              </h4>
              <span className="text-xs sm:text-sm font-mono font-bold text-cyan-400 whitespace-nowrap flex-shrink-0">
                {formatCurrencyUAH(payout.totalInterLevelCommissionUAH, 2)}
              </span>
            </div>

            {payout.lineBreakdowns.length > 0 ? (
              <div className="space-y-2.5">
                {payout.lineBreakdowns.map((line) => (
                  <DirectLineItem
                    key={line.lineLeaderId}
                    item={line}
                    mentorLevel={payout.level}
                    onSelectLeader={onSelectLeader}
                  />
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-4 text-center text-xs text-text-muted border border-dashed border-slate-700">
                Прямі паралельні гілки поки відсутні (увесь дохід формується з особистих продажів).
              </div>
            )}
          </div>

          {/* SECTION 3: Director & Co-Owner Bonuses */}
          {(isDirector || isCoOwner) && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-xs sm:text-sm font-bold text-amber-300 uppercase tracking-wider flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span>3. Кваліфікаційні бонуси (Директорські виплати)</span>
                </h4>
                <span className="text-xs sm:text-sm font-mono font-bold text-amber-400 whitespace-nowrap flex-shrink-0">
                  {formatCurrencyUAH(payout.directorBonusUAH + payout.coOwnerBonusUAH, 2)}
                </span>
              </div>

              <div className="space-y-2.5">
                {/* Director Bonus Row (L6–8) */}
                {isDirector && (
                  <div className="glass-card rounded-xl p-3.5 border border-amber-500/20 bg-amber-500/5 space-y-2 text-xs">
                    <div className="space-y-1">
                      <div className="font-semibold text-amber-200 flex items-center gap-1.5">
                        <Award className="w-4 h-4 text-amber-400" />
                        Директорський бонус (50 грн/Е)
                      </div>
                      <div className="text-text-secondary">
                        Кваліфікований обіг (особистий + структури ≤ Р5):{' '}
                        <strong className="font-mono text-text-primary">
                          {formatAE(payout.directorBonusQualifiedTurnoverAE)}
                        </strong>
                      </div>
                    </div>

                    <div className="font-mono bg-slate-900/80 px-3 py-1.5 rounded-lg border border-amber-500/20 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-text-muted">
                        {formatAE(payout.directorBonusQualifiedTurnoverAE, 0)} × 50.00 ₴
                      </div>
                      <div className="text-sm font-bold text-amber-400 whitespace-nowrap">
                        = {formatCurrencyUAH(payout.directorBonusUAH, 2)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Co-Owner Bonus Row (L8) */}
                {isCoOwner && (
                  <div className="glass-card rounded-xl p-3.5 border border-yellow-500/30 bg-yellow-500/5 space-y-2 text-xs">
                    <div className="space-y-1">
                      <div className="font-semibold text-yellow-300 flex items-center gap-1.5">
                        <Crown className="w-4 h-4 text-yellow-400" />
                        Співвласницький бонус (100 грн/Е)
                      </div>
                      <div className="text-text-secondary">
                        Кваліфікований обіг (особистий + структури ≤ Р7):{' '}
                        <strong className="font-mono text-text-primary">
                          {formatAE(payout.coOwnerBonusQualifiedTurnoverAE)}
                        </strong>
                      </div>
                    </div>

                    <div className="font-mono bg-slate-900/80 px-3 py-1.5 rounded-lg border border-yellow-500/20 flex items-center justify-between gap-2">
                      <div className="text-[11px] text-text-muted">
                        {formatAE(payout.coOwnerBonusQualifiedTurnoverAE, 0)} × 100.00 ₴
                      </div>
                      <div className="text-sm font-bold text-yellow-300 whitespace-nowrap">
                        = {formatCurrencyUAH(payout.coOwnerBonusUAH, 2)}
                      </div>
                    </div>
                  </div>
                )}

                {/* 100% Payout Guarantee for Bonuses */}
                <div className="text-[11px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>
                    <strong>Зверніть увагу:</strong> Директорський (50 грн/Е) та Співвласницький (100 грн/Е) бонуси завжди виплачуються на 100% у повному розмірі (коефіцієнт 80% до них не застосовується).
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 4: Total Gross Income Summary */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/15 rounded-xl p-4 sm:p-5 space-y-2.5 shadow-lg">
            <div className="text-xs text-text-secondary uppercase tracking-wider font-semibold flex items-center gap-1.5">
              <Calculator className="w-4 h-4 text-accent-neon flex-shrink-0" />
              <span>Загальний нарахований дохід (Брутто):</span>
            </div>

            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="text-2xl sm:text-3xl font-extrabold font-mono text-white tracking-tight whitespace-nowrap">
                {formatCurrencyUAH(payout.totalGrossIncomeUAH, 2)}
              </div>
              <div className="text-xs sm:text-sm font-mono text-cyan-300/90 whitespace-nowrap">
                ≈ {formatCurrencyUSD(payout.totalGrossIncomeUSD, 0)}
              </div>
            </div>

            <div className="text-[11px] text-text-muted pt-2 border-t border-white/10 leading-relaxed">
              Особисті ({formatCurrencyUAH(payout.personalCommissionUAH, 0)}) + Міжрівневі ({formatCurrencyUAH(payout.totalInterLevelCommissionUAH, 0)})
              {(payout.directorBonusUAH > 0 || payout.coOwnerBonusUAH > 0) &&
                ` + Бонуси (${formatCurrencyUAH(payout.directorBonusUAH + payout.coOwnerBonusUAH, 0)})`}
            </div>
          </div>

          {/* SECTION 5: Tax Breakdown and Net Income Card */}
          <TaxSummaryCard payout={payout} />
        </CardContent>
      </Card>
    </div>
  );
};
